import { Button, useMutation, useNotify } from "react-admin";
import SaveIcon from "@material-ui/icons/Save";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
} from "@material-ui/core";
import { useFormState, useField } from "react-final-form";
import _ from "lodash";
import { Fragment, useEffect, useState } from "react";

interface ISaveRouteButton {
  routeLocationData: any;
  handleCloseRoutePanel: any;
  isUpdateRoutePlan?: boolean;
}

const useStyles = makeStyles({
  sidePanelSaveRouteButton: {
    backgroundColor: "#4F5B7B",
    boxShadow: "0 4px 8px rgb(79 91 123 / 30%)",
    padding: "8px",
    margin: "12px 8px 0 0",
    float: "right",
    minWidth: "10px",
    "& .MuiButton-textSizeSmall": {
      padding: 0,
    },
    "& .MuiButton-label > .MuiSvgIcon-root": {
      fontSize: 25,
    },
    "&:hover": {
      backgroundColor: "#4F5B7B !important",
    },
  },
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  table: {
    marginTop: "10px",
    minWidth: 650,
    padding: "1rem",
  },
});

const CustomConfirmButton = ({
  routeLocationData,
  handleCloseRoutePanel,
  isUpdateRoutePlan,
}: ISaveRouteButton) => {
  const [isDisable, setIsDisable] = useState<boolean>(true);
  const { values } = useFormState();
  const notify = useNotify();

  const routesField = useField("routes");
  const nextRoutePlanRoutesField = useField("nextRoutePlan.routes");
  const busSchedulePlanId = useField(`id`);
  const routeField = useField("route");

  useEffect(() => {
    if (
      values.route?.name &&
      values.route?.startTime &&
      values.route?.endTime &&
      // values.route?.driverId &&
      // values.route?.busAssistantId &&
      // values.route?.vehicleId &&
      routeLocationData.length > 0
    ) {
      setIsDisable(false);
    } else {
      setIsDisable(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const [handleSaveRoute, { loading }] = useMutation(
    {
      type: "customUpdate",
      resource: `busSchedulePlans/${busSchedulePlanId.input.value}/routes/0`, // new route always id 0
      payload: {
        data: {
          id: 0,
          name: values.route.name,
          busBayNumber: values.route.busBayNumber,
          startTime: values.route.startTime,
          endTime: values.route.endTime,
          driverId: values.route.driverId,
          busAssistantId: values.route.busAssistantId,
          vehicleId: values.route.vehicleId,
          backupDriverIds: values.route.backupDriverIds
            ? values.route.backupDriverIds
            : values.route.backupDrivers?.length > 0
            ? values.route.backupDrivers.map(
                (backupDriver: any) => backupDriver.id
              )
            : [],
          backupVehicleIds: values.route.backupVehicleIds
            ? values.route.backupVehicleIds
            : values.route.backupVehicles?.length > 0
            ? values.route.backupVehicles.map(
                (backupVehicle: any) => backupVehicle.id
              )
            : [],
          backupBusAssistantIds: values.route.backupBusAssistantIds
            ? values.route.backupBusAssistantIds
            : values.route.backupBusAssistants?.length > 0
            ? values.route.backupBusAssistants.map(
                (backupBA: any) => backupBA.id
              )
            : [],
          routeLocations: values.route.routeLocations,
        },
      },
    },
    {
      onSuccess: ({ data }) => {
        // then set to form data
        var newRoute = {
          id: data.id,
          ...values.route,
        };

        nextRoutePlanRoutesField.input.onChange([
          ...nextRoutePlanRoutesField.input.value,
          ...[newRoute],
        ]);
        routeField.input.onChange({});

        notify(`Successfully save the route.`, "info");
        handleCloseRoutePanel();
      },
      onFailure: (error) => {
        notify(`Failed save the route, error: ${error.message}`, "warning");
      },
    }
  );

  const convertStudentData = (routeLocationData: any) => {
    let locations: any = [];

    routeLocationData.forEach((location: any) => {
      const temp = _.cloneDeep(location.students);
      const studentIds = temp.map((item: any) => {
        return item ? item.id : item;
      });

      location.studentIds = studentIds;

      locations.push(location);
    });

    return { routeLocations: locations };
  };

  useEffect(() => {
    const routeLocations = convertStudentData(routeLocationData);

    if (!values.route?.backupDriverIds) {
      routeField.input.onChange({
        ...routeField.input.value,
        ...routeLocations,
        ...{ backupDriverIds: [] },
      });
    } else if (!values.route?.backupVehicleIds) {
      routeField.input.onChange({
        ...routeField.input.value,
        ...routeLocations,
        ...{ backupVehicleIds: [] },
      });
    } else if (!values.route?.backupBusAssistantIds) {
      routeField.input.onChange({
        ...routeField.input.value,
        ...routeLocations,
        ...{ backupBusAssistantIds: [] },
      });
    } else {
      routeField.input.onChange({
        ...routeField.input.value,
        ...routeLocations,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeLocationData]);

  const handleSave = async () => {
    if (isUpdateRoutePlan) {
      // send directly to API
      handleSaveRoute();
    } else {
      routesField.input.onChange([
        ...routesField.input.value,
        ...[values.route],
      ]);
      routeField.input.onChange({});

      handleCloseRoutePanel();
    }
  };

  return (
    <Button
      color="primary"
      onClick={() => handleSave()}
      disabled={isDisable}
      label={loading ? "Please wait..." : "Confirm"}
      autoFocus
    >
      <SaveIcon />
    </Button>
  );
};

const SaveRouteButton = ({
  routeLocationData,
  handleCloseRoutePanel,
  isUpdateRoutePlan,
}: ISaveRouteButton) => {
  const [open, setOpen] = useState<boolean>(false);
  const classes = useStyles();

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Fragment>
      <Button
        className={classes.sidePanelSaveRouteButton}
        onClick={handleOpenDialog}
      >
        <SaveIcon />
      </Button>
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">Save Route</DialogTitle>
        <DialogContent>
          <DialogContentText color="initial">
            Are you sure to save route? this action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" label="Back" />
          <CustomConfirmButton
            routeLocationData={routeLocationData}
            handleCloseRoutePanel={handleCloseRoutePanel}
            isUpdateRoutePlan={isUpdateRoutePlan}
          />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default SaveRouteButton;
